import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { UnifiedMetricCard, UnifiedSectionHeader } from '../../design/UnifiedComponents';
import { formatTrCount, unifiedUI } from '../../design/unified';
import { teacherTypography } from '../../design/typography';
import { assessmentActionLabel, assessmentProgress, assessmentStatusLabel, summarizeAssessmentList } from './courseAssessmentPresentation';

const scopeLabel = exam => exam.grade_level ? `${exam.grade_level}. Sınıf` : exam.class_name || 'Şube bilgisi yok';
const scoringLabel = exam => exam.scoring_mode === 'development' ? 'Gelişim Düzeyi'
  : exam.scoring_mode === 'test' ? `${formatTrCount(exam.total_questions)} soruluk test` : '100 üzerinden puan';
const dateLabel = exam => exam.exam_date ? new Date(exam.exam_date).toLocaleDateString('tr-TR') : 'Tarih belirtilmedi';

function ResultProgress({ exam, progress }) {
  return <Stack spacing={0.75}>
    <Chip size="small" label={assessmentStatusLabel(progress)} sx={{
      alignSelf: 'flex-start', height: 'auto', minHeight: 28, fontSize: 13, fontWeight: 600,
      bgcolor: !progress.known ? '#F0F2F7' : progress.complete ? '#EDF9F2' : '#FFF4E7',
      color: !progress.known ? unifiedUI.secondary : progress.complete ? '#176846' : '#825020',
      '& .MuiChip-label': { py: 0.5, whiteSpace: 'normal' },
    }} />
    {progress.known ? <>
      <Typography sx={{ ...teacherTypography.metadata, fontVariantNumeric: 'tabular-nums' }}>
        <Box component="span">{formatTrCount(progress.entered)}/{formatTrCount(progress.total)}</Box> sonuç girildi (%{formatTrCount(progress.percent)})
      </Typography>
      <LinearProgress variant="determinate" value={progress.percent}
        aria-label={`${exam.title} sonuç girişi tamamlanma yüzdesi`}
        sx={{ height: 6, borderRadius: 99, bgcolor: '#E8EAF3', '& .MuiLinearProgress-bar': { bgcolor: unifiedUI.purple } }} />
    </> : <Typography sx={teacherTypography.metadata}>Sonuç ilerlemesi için öğrenci sayısı bekleniyor.</Typography>}
  </Stack>;
}

/** Opt-in Teacher list pattern. Only saved API counts belong in this view. */
export default function CourseAssessmentList({ exams, selectedExamId, disabled, onOpen, renderMenu }) {
  const narrowViewport = useMediaQuery('(max-width:1120px)');
  const container = useRef(null);
  const [narrowContainer, setNarrowContainer] = useState(false);
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => setNarrowContainer(entry.contentRect.width < 960));
    observer.observe(container.current);
    return () => observer.disconnect();
  }, []);
  const compact = narrowViewport || narrowContainer;
  const summary = summarizeAssessmentList(exams);
  const items = exams.map(exam => ({ exam, progress: assessmentProgress(exam.completed_count, exam.student_count) }));
  const pending = items.find(item => item.progress.known && item.progress.missing > 0);
  const openButton = (exam, progress, card = false) => <Button variant="outlined"
    fullWidth={card} disabled={disabled} disableRipple
    aria-label={`${exam.title} — ${assessmentActionLabel(progress)}`}
    onClick={event => { event.stopPropagation(); onOpen(exam); }}
    sx={{ ...(card ? { mt: 'auto' } : {}),
      minHeight: 44, textTransform: 'none', fontWeight: 600, color: unifiedUI.purple,
      borderColor: unifiedUI.purple, whiteSpace: 'nowrap',
      '&:focus-visible': { outline: `3px solid ${unifiedUI.purple}`, outlineOffset: 2 },
    }}>{assessmentActionLabel(progress)}</Button>;

  return <Stack ref={container} spacing={{ xs: 1.5, sm: 2.5 }} sx={{ containerType: 'inline-size', containerName: 'assessment-list', minWidth: 0 }}>
    <Box data-testid="unified-assessment-kpis" sx={{ display: 'grid', gap: { xs: 1, sm: 1.5 },
      gridTemplateColumns: 'repeat(2,minmax(0,1fr))',
      '@media (max-width:359px)': { gridTemplateColumns: 'minmax(0,1fr)' },
      '@media (min-width:769px)': { gridTemplateColumns: 'repeat(3,minmax(0,1fr))' },
      '& > :last-child': { '@media (min-width:360px) and (max-width:768px)': { gridColumn: '1 / -1' } },
    }}>
      <UnifiedMetricCard compact label="Değerlendirmeler" value={formatTrCount(summary.totalAssessments)} help="Listelenen değerlendirme" />
      <UnifiedMetricCard compact label="Tamamlananlar" value={formatTrCount(summary.completedAssessments)} help="Tüm öğrencilerin sonucu girildi" />
      <UnifiedMetricCard compact label="Bekleyen sonuçlar" value={summary.knownAssessments ? formatTrCount(summary.pendingResults) : '—'}
        help={summary.unknownAssessments
          ? `${formatTrCount(summary.unknownAssessments)} değerlendirmede öğrenci sayısı eksik; toplama dahil değil.`
          : 'Henüz sonucu girilmeyen öğrenciler'} emphasis />
    </Box>
    {pending && <Box data-testid="unified-next-action" sx={{
      px: { xs: 1.75, sm: 2.5 }, py: { xs: 1.25, sm: 2 }, borderLeft: `4px solid ${unifiedUI.purple}`,
      bgcolor: unifiedUI.purpleSurface, borderRadius: '0 14px 14px 0',
      display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 1.5 },
      justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' },
    }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ ...teacherTypography.metricLabel, color: unifiedUI.purple }}>Sıradaki işlem</Typography>
        <Typography sx={{ ...teacherTypography.cardTitle, mt: 0.5, overflowWrap: 'anywhere' }}>
          {pending.exam.title} · {formatTrCount(pending.progress.missing)} sonuç bekliyor
        </Typography>
      </Box>
      <Button disabled={disabled} onClick={() => onOpen(pending.exam)} disableRipple
        sx={{ ...unifiedUI.mainAction, px: 2, flexShrink: 0 }}>Sonuçları tamamla</Button>
    </Box>}
    <Box>
      <UnifiedSectionHeader title="Değerlendirme listesi" description="Sonuçları açın; güncelleme ve silme için diğer işlemler menüsünü kullanın."
        action={<Typography sx={teacherTypography.metadata}>{formatTrCount(exams.length)} değerlendirme</Typography>} />
      {compact ? <Box data-testid="unified-assessment-card-list" sx={{ display: 'grid', gap: 2, mt: 2,
        gridTemplateColumns: 'minmax(0,1fr)', '@container assessment-list (min-width:691px)': { gridTemplateColumns: 'repeat(2,minmax(0,1fr))' } }}>
        {items.map(({ exam, progress }) => <Paper component="article" key={exam.id} data-testid="unified-assessment-card"
          elevation={0} sx={{ ...unifiedUI.surfaceCard, p: 2, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          <Stack direction="row" alignItems="flex-start" spacing={0.5}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography data-testid="course-assessment-title" component="h3" sx={{ ...teacherTypography.cardTitle, overflowWrap: 'anywhere' }}>{exam.title}</Typography>
              <Typography sx={{ ...teacherTypography.metadata, mt: 0.75, overflowWrap: 'anywhere' }}>
                {[exam.lesson_name, scopeLabel(exam), dateLabel(exam)].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
            {renderMenu(exam)}
          </Stack>
          <Typography sx={teacherTypography.metadata}>{scoringLabel(exam)}</Typography>
          <ResultProgress exam={exam} progress={progress} />
          {openButton(exam, progress, true)}
        </Paper>)}
      </Box> : <TableContainer component={Paper} elevation={0} sx={{ ...unifiedUI.surfaceCard, mt: 2 }}>
        <Table size="small" aria-label="Ders değerlendirmeleri">
          <TableHead><TableRow sx={{ bgcolor: unifiedUI.surfaceSubtle, '& .MuiTableCell-root': teacherTypography.tableHeader }}>
            <TableCell>Değerlendirme</TableCell><TableCell>Sınav Tarihi</TableCell><TableCell>Kapsam</TableCell>
            <TableCell>Tür</TableCell><TableCell>Sonuç girişi</TableCell><TableCell align="right">İşlemler</TableCell>
          </TableRow></TableHead>
          <TableBody>{items.map(({ exam, progress }) => <TableRow key={exam.id} hover
            selected={String(selectedExamId) === String(exam.id)} tabIndex={disabled ? -1 : 0}
            onClick={() => !disabled && onOpen(exam)}
            onKeyDown={event => {
              if (event.target !== event.currentTarget || disabled) return;
              if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpen(exam); }
            }} sx={{ cursor: disabled ? 'default' : 'pointer',
              '&:focus-visible': { outline: `3px solid ${unifiedUI.purple}`, outlineOffset: -3 },
              '& .MuiTableCell-root': { py: 2, overflowWrap: 'anywhere' } }}>
            <TableCell sx={{ maxWidth: 320 }}><Typography data-testid="course-assessment-title" sx={teacherTypography.studentName}>{exam.title}</Typography>
              {exam.lesson_name && <Typography sx={teacherTypography.metadata}>{exam.lesson_name}</Typography>}</TableCell>
            <TableCell>{dateLabel(exam)}</TableCell><TableCell>{scopeLabel(exam)}</TableCell>
            <TableCell>{scoringLabel(exam)}</TableCell><TableCell sx={{ minWidth: 180 }}><ResultProgress exam={exam} progress={progress} /></TableCell>
            <TableCell><Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
              {openButton(exam, progress)}{renderMenu(exam)}
            </Stack></TableCell>
          </TableRow>)}</TableBody>
        </Table>
      </TableContainer>}
    </Box>
  </Stack>;
}
